import { Close } from '@mui/icons-material';
import { Box, Button, CircularProgress, Container, Grow, IconButton, Modal, Stack, Typography } from '@mui/material';
import Compressor from 'compressorjs';
import { useState } from 'react';
import congratsPoint from '../../assets/customicons/congratsPoint.png';
import congratsPoint_25 from '../../assets/customicons/congratsPoint_25.png';
import congratsPoint_50 from '../../assets/customicons/congratsPoint_50.png';
import congratsPoint_75 from '../../assets/customicons/congratsPoint_75.png';
import walletCoin from '../../assets/customicons/walletCoin.png';
import ProgressTracker from '../charts/ProgressTracker';

export default function ImageUpload({ uid, open, handleClose, cid, challengeTitle, challengePoints, challengeType, challengeFrequency }) {
    // Inputs
    const [selectedFile, setSelectedFile] = useState(null);
    const [selectedFileBase64, setFileBase64] = useState(null);
    const [selectedFileType, setFileType] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);

    // Flags
    const [isSubmitting, setSubmitting] = useState(false); 
    const [isSuccessful, setSuccess] = useState(false);
    const [stage, setStage] = useState(1);

    // Specific for streak-based challenges
    const [isStreakBased, setStreakBased] = useState(false); 
    const [streakCount, setStreakCount] = useState(3);
    const [streakTotal, setStreakTotal] = useState(4);

    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            new Compressor(file, {
                convertTypes: ['image/heic', 'image/heif', 'image/webp', 'image/bmp', 'image/gif', 'image/tiff', 'image/svg+xml'],
                convertSize: 5000000,
                success: (compressedFile) => {
                    const reader = new FileReader();
                    reader.onload = () => {
                        const resultfile = reader.result;
                        const base64_string = resultfile.split(',')[1]
        
                        setSelectedFile(compressedFile);
                        setFileBase64(base64_string);
                        setFileType(compressedFile.type); // e.g., "image/jpeg"
        
                        setImagePreview(resultfile);
                    };
                    reader.readAsDataURL(compressedFile);
                    setStage(2);
                }
            })
        }
    };

    const handleModalClose = () => {
        setStage(1);
        handleClose();
    };

    const handleSubmit = () => {
        setStage(3);
        setSubmitting(true);

        const body = {
            "httpMethod": "POST", // For routing
            "resourcePath": "/challenges", // For routing
            'cid': cid,
            'uid': uid,
            'file': selectedFileBase64,
            'fileType': selectedFileType,
            'challengeTitle': challengeTitle,
            'challengePoints': challengePoints,
            'challengeType': challengeType,
            'challengeFrequency': challengeFrequency,
            'fileMeta': extractFileMeta(selectedFile),
        };
        // console.log(body)

        const url = process.env.REACT_APP_UPLOAD_LAMBDA;
        fetch(url, {
            method: 'POST',
            body: JSON.stringify(body),
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            },
        }).then(response => {
            return response.json();
        }).then(data => {
            let results = JSON.parse(data?.body || '{}');

            // Set display flags
            setSubmitting(false);
            setSuccess(results?.isSuccess);

            // For streak based challenges
            setStreakBased(challengeType === 'weekly' || challengeType === 'day');
            setStreakCount(results?.streakCount);
            setStreakTotal(challengeFrequency);

            // Reset variables
            setImagePreview(null); 
            setSelectedFile(null);
        }).catch(error => {
            console.error('There has been a problem with your fetch operation:', error);
            return {};
        });
    };
    
    return (
        <Modal open={open} onClose={handleModalClose}>
            <Box className="upload-image-container" >
                <Container maxWidth='xs' sx={{ height: '100%', py: 2 }}>
                <Stack spacing={2}>
                    <Box className="cross-container">
                        <IconButton onClick={handleModalClose}>
                            <Close />
                        </IconButton>
                    </Box>
                    {
                        stage === 1 ?
                        <>
                            <Button className="pill_btn challenge_btn" onClick={() => document.getElementById('gallery-input').click()}>
                                Select from Photo Gallery
                            </Button>
                            <input
                                type="file"
                                id="gallery-input"
                                style={{ display: 'none' }}
                                onChange={handleFileChange}
                                accept="image/jpeg, image/png" 
                            />
                        </>
                        :
                        stage === 2 ?
                        <Box className="confirm-upload-container">
                            {imagePreview && (
                                <Box>
                                    <img src={imagePreview} alt="Uploaded Preview" className='upload-image' />
                                </Box>
                            )}

                            <Button className="pill_btn submit-btn" onClick={handleSubmit}>
                                Submit
                            </Button>
                        </Box>
                        :
                        stage === 3 ?
                        <Box className="congrats-container">
                            {
                                isSubmitting ?
                                <Box className='centered-container'>
                                    <CircularProgress color='secondary' />
                                </Box>
                                :
                                isSuccessful ? (
                                    ( !isStreakBased || (isStreakBased && streakCount===streakTotal)) ?
                                    <>
                                        <Grow
                                            in={isSuccessful}
                                            {...(isSuccessful ? { timeout: 500 } : {})}
                                        >
                                            <img src={congratsPoint} alt="congrats-coin" className='upload-image' />
                                        </Grow>
                                        <Typography variant="h5">
                                            Well Done!
                                        </Typography>
                                        <Typography variant="body1">
                                            You have earned <b>{challengePoints} points</b><br />
                                            for completing this challenge:<br />
                                            <b>{challengeTitle}</b>.
                                        </Typography>
                                        
                                        <Box className='centered-x'>
                                            <img src={walletCoin} style={{ width: 50 }} alt="coin icon" />
                                            <Typography variant='caption'>
                                                &emsp;<b>{challengePoints} coins</b> have also been added to your wallet.
                                            </Typography>
                                        </Box>
                                    </>
                                    :
                                    <>
                                        <Grow
                                            in={isSuccessful}
                                            {...(isSuccessful ? { timeout: 500 } : {})}
                                        >
                                            <img src={
                                                (streakCount/streakTotal) > 0.75 ? congratsPoint
                                                : (streakCount/streakTotal) > 0.5 ? congratsPoint_75
                                                : (streakCount/streakTotal) > 0.25 ? congratsPoint_50
                                                : (streakCount/streakTotal) <= 0.25 ? congratsPoint_25
                                                :  congratsPoint
                                            } alt="congrats-coin" className='upload-image' />
                                        </Grow>
                                        <Typography variant="h5">
                                            You're getting there!
                                        </Typography>
                                        <Typography variant="body1">
                                            Complete your streak to earn <br />
                                            <b>{challengePoints} points</b> for this challenge:<br />
                                            <b>{challengeTitle}</b>.
                                        </Typography>
                                        <ProgressTracker 
                                            withPopup={false} 
                                            isStreakType={isStreakBased} 
                                            streakType={challengeType}
                                            streakCount={streakCount}
                                            streakTotal={streakTotal}
                                        />
                                    </>
                                )
                                :
                                <>
                                <Typography variant="h5">
                                    Error
                                </Typography>
                                <Typography variant="body1">
                                    Sorry, something went wrong in your submission. Please try again later.
                                </Typography>
                                <Typography variant="body1">
                                    If the issue persists, please contact <a href={`mailto:${process.env.REACT_APP_HELP_EMAIL}`}>{process.env.REACT_APP_HELP_EMAIL}</a>.
                                </Typography>
                                </>
                            }
                        </Box>
                        :
                        <></>
                    }
                </Stack>
                </Container>
            </Box>
        </Modal>
    );
}

const extractFileMeta = (file) => {
    const fileMetadata = {
        name: file.name,
        size: file.size,
        type: file.type,
        lastModified: file.lastModifiedDate
    };
    return fileMetadata;
}
